import ArrayFormControl from '@/pages/WorkflowsCatalog/components/run/controls/array-form-control';
import BooleanFormControl from '@/pages/WorkflowsCatalog/components/run/controls/boolean-form-control';
import DefaultFormControl from '@/pages/WorkflowsCatalog/components/run/controls/default-form-control';
import EnumFormControl from '@/pages/WorkflowsCatalog/components/run/controls/enum-form-control';
import StringFormControl from '@/pages/WorkflowsCatalog/components/run/controls/string-form-control';
import { extractEnumInfo } from '@/pages/WorkflowsCatalog/utils/schemaUtils';
import type { ControllerRenderProps, FieldValues, Path, RegisterOptions } from 'react-hook-form';

type CreateControlArgs<T extends FieldValues> = {
  property: any;
  field: ControllerRenderProps<T, Path<T>>;
};

export const createFormControl = <T extends FieldValues>({
  property,
  field,
}: CreateControlArgs<T>) => {
  const enumInfo = extractEnumInfo(property);
  const propertyType = Array.isArray(enumInfo.type)
    ? enumInfo.type.find((t: string) => t !== 'null') || enumInfo.type[0]
    : enumInfo.type ||
      (Array.isArray(property.type)
        ? property.type.find((t: string) => t !== 'null') || property.type[0]
        : property.type);

  if (enumInfo.enum) {
    return <EnumFormControl<T> field={field} options={enumInfo.enum} />;
  }
  if (propertyType === 'boolean') {
    return <BooleanFormControl<T> field={field} />;
  }
  if (propertyType === 'array') {
    const itemType: 'string' | 'number' | 'boolean' | undefined = Array.isArray(
      property.items?.type
    )
      ? property.items.type.find((t: string) => t !== 'null')
      : property.items?.type;
    return <ArrayFormControl<T> field={field} itemType={itemType as any} />;
  }
  if (propertyType === 'string') {
    return <StringFormControl<T> field={field} />;
  }
  return <DefaultFormControl<T> field={field} propertyType={propertyType} />;
};

export const getPropertyType = (property: any) => {
  const enumInfo = extractEnumInfo(property);
  return enumInfo.type || property.type;
};

export const createRequiredRules = <T extends FieldValues, N extends Path<T>>(
  property: any,
  isRequired: boolean
): RegisterOptions<T, N> => {
  if (!isRequired) return {};
  const enumInfo = extractEnumInfo(property);
  // Fields with defaults don't need client-side required validation
  if (enumInfo.defaultValue !== undefined) return {};
  const propertyType = Array.isArray(enumInfo.type)
    ? enumInfo.type.find((t: string) => t !== 'null') || enumInfo.type[0]
    : enumInfo.type ||
      (Array.isArray(property.type)
        ? property.type.find((t: string) => t !== 'null') || property.type[0]
        : property.type);

  if (propertyType === 'string') {
    return { required: 'This field is required' } as RegisterOptions<T, N>;
  }
  if (propertyType === 'array') {
    return {
      validate: (v: unknown) =>
        (Array.isArray(v) && v.length > 0) || 'At least one item is required',
    } as RegisterOptions<T, N>;
  }
  if (propertyType === 'boolean') {
    return {
      validate: (v: unknown) => typeof v === 'boolean' || 'This field is required',
    } as RegisterOptions<T, N>;
  }
  return {
    validate: (v: unknown) =>
      (v !== undefined && v !== null && v !== '') || 'This field is required',
  } as RegisterOptions<T, N>;
};
